import {
  autoUpdate,
  AutoUpdateOptions,
  flip,
  FlipOptions,
  offset,
  shift,
  ShiftOptions,
  useDismiss,
  useFloating,
  UseFloatingOptions,
  useTransitionStyles,
} from '@floating-ui/react';

import {useRef} from 'react';

import {useBoolean, useOutsideClick} from 'shared/web';

import {Placement} from '../../../types/Placement';
import {AnimatedPopper, AnimatedPopperProps} from '../AnimatedPopper';

/**
 * Based on https://floating-ui.com/docs/autoupdate#options
 */
const DEFAULT_AUTO_UPDATE_OPTIONS = {
  ancestorResize: true,
  ancestorScroll: true,
  elementResize: true,
  layoutShift: true,
} as const satisfies AutoUpdateOptions;

interface UseAnimatedPopperProps {
  placement?: Placement;
  gutter?: number;
  /**
   * Prevents the popper from closing on outside click
   */
  isOutsideClickIgnored?: boolean;
  strategy?: 'absolute' | 'fixed';
  autoUpdateOptions?: AutoUpdateOptions | 'default';
  /**
   * Use a portal to render the popper outside the DOM hierarchy.
   * Useful when the popper is inside a stacking context (e.g., position: sticky).
   */
  usePortal?: boolean;
  closeOnScroll?: boolean;
  /**
   * Options for the shift() middleware (viewport padding and cross-axis shifting).
   * Use e.g. { padding: 8, crossAxis: true } to keep the popup inside the viewport on small screens.
   */
  shiftOptions?: ShiftOptions;
  /**
   * Run flip() before shift(). Needed when shiftOptions.crossAxis is enabled, otherwise the
   * cross-axis shift pulls the popper over its reference before flip() can see the overflow.
   * https://floating-ui.com/docs/flip#combining-with-shift
   */
  isFlipPrioritized?: boolean;
  flipOptions?: FlipOptions;
  /**
   * Predicate to skip outside-click close for specific event targets.
   * Useful when the popper contains components that render menus in a portal
   * (e.g., Choice with menuInPortal) — clicks on those menus would otherwise
   * be treated as outside clicks and close the popper.
   */
  shouldIgnoreOutsideClick?: (target: Element) => boolean;
}

interface UseAnimatedPopperReturnType {
  isOpen: boolean;
  popperProps: AnimatedPopperProps;
  Popper: typeof AnimatedPopper;
  openPopper: () => void;
  closePopper: () => void;
  togglePopper: () => void;
  referenceRef: <T extends Element>(node: T | null) => void;
}

const DEFAULT_PROPS: UseAnimatedPopperProps = {
  placement: 'top-start',
  gutter: 0,
  strategy: 'absolute',
};

export const useAnimatedPopper = (
  props: UseAnimatedPopperProps = DEFAULT_PROPS
): UseAnimatedPopperReturnType => {
  const clickRef = useRef<HTMLDivElement>(null);
  const [isOpen, onOpen, onClose, onToggle] = useBoolean(false);

  const whileElementsMounted: UseFloatingOptions['whileElementsMounted'] | undefined =
    props.autoUpdateOptions
      ? (referenceEl, floatingEl, update) => {
          const autoUpdateOptions =
            props.autoUpdateOptions === 'default'
              ? DEFAULT_AUTO_UPDATE_OPTIONS
              : props.autoUpdateOptions;

          return autoUpdate(referenceEl, floatingEl, update, autoUpdateOptions);
        }
      : undefined;

  const {refs, floatingStyles, context} = useFloating({
    whileElementsMounted,
    placement: props.placement,
    middleware: props.isFlipPrioritized
      ? [offset(props.gutter), flip(props.flipOptions), shift(props.shiftOptions)]
      : [offset(props.gutter), shift(props.shiftOptions), flip(props.flipOptions)],
    strategy: props.strategy,
    open: isOpen,
    onOpenChange: (open) => (open ? onOpen() : onClose()),
  });

  const {styles: transitionStyles} = useTransitionStyles(context);

  useOutsideClick({
    ref: clickRef,
    handler: (event) => {
      const target = event.target instanceof Element ? event.target : null;
      if (target && props.shouldIgnoreOutsideClick?.(target)) {
        return;
      }
      onClose();
    },
    enabled: !props.isOutsideClickIgnored,
  });

  useDismiss(context, {
    ancestorScroll: props.closeOnScroll ?? false,
    outsidePress: (event) => {
      const target = event.target instanceof Element ? event.target : null;
      if (target && props.shouldIgnoreOutsideClick?.(target)) {
        return false;
      }
      return true;
    },
    // NOTE: Possibly replace useOutsideClick with outsidePress
    // - Add useInteractions hook, update consumers (might conflict with existing handlers
    // - Refactoring is not worth it right now, maybe in the future if we needed more interactions via useInteractions hook
  });

  return {
    isOpen,
    openPopper: onOpen,
    closePopper: onClose,
    togglePopper: onToggle,
    referenceRef: refs.setReference,
    Popper: AnimatedPopper,
    popperProps: {
      isOpen,
      floatingRef: refs.setFloating,
      clickRef,
      floatingStyles,
      transitionStyles,
      // eslint-disable-next-line react/react-compiler -- pre-existing, tracked by T20-137202
      usePortal: props.usePortal,
    },
  };
};
